import { motion } from 'framer-motion';
import { Clock, Link2, FileOutput, ShieldCheck } from 'lucide-react';

const benefits = [
  {
    icon: Clock,
    accent: 'from-primary/20 to-primary/5',
    iconBg: 'bg-primary/10 text-primary',
    title: 'First-Pass Insights in <2 Minutes',
    stat: '<2 min',
    statLabel: 'per source',
    body: 'Raw transcripts become coded patterns and initial themes instantly. Break through blank-page overwhelm with a defensible starting point.',
    footnote: '*~1.8–2 min for a first coded pass on a 50-page transcript (~100K characters). Internal benchmarks with typical interview data.',
  },
  {
    icon: Link2,
    accent: 'from-emerald-500/15 to-emerald-500/5',
    iconBg: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    title: 'Ironclad Quote-Level Traceability',
    stat: '100%',
    statLabel: 'linked evidence',
    body: 'Every code, theme, and finding links to exact quotes, lines, and timestamps. Build a transparent audit trail that satisfies reviewers.',
  },
  {
    icon: FileOutput,
    accent: 'from-violet-500/15 to-violet-500/5',
    iconBg: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
    title: 'Seamless Draft Exports',
    stat: '19+',
    statLabel: 'export formats',
    body: 'Export ready-to-refine drafts in Word, PDF, CSV, or custom formats. Compatible with Zotero, Mendeley, Notion, and your full stack.',
  },
  {
    icon: ShieldCheck,
    accent: 'from-amber-500/15 to-amber-500/5',
    iconBg: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    title: 'Secure & Private by Default',
    stat: '0',
    statLabel: 'training on your data',
    body: 'Your data is never used for model training. Export a ready-made AI disclosure appendix for ethics boards and reviewers.',
  },
];

const tileVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.08 * i, duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  }),
};

export function BenefitsSection() {
  return (
    <section
      id="benefits"
      className="relative py-16 sm:py-24 md:py-32 scroll-mt-24 overflow-hidden"
    >
      {/* Background */}
      <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-muted/10 to-transparent" />
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-2xl mx-auto mb-12 md:mb-16"
        >
          <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-evidence/10 border border-evidence/20 mb-5">
            <span className="text-[10px] md:text-xs font-semibold text-evidence uppercase tracking-wider">
              Why Verithos
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-balance leading-tight">
            Rigorous Insights,{' '}
            <span className="text-primary">Accelerated</span>
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl mx-auto">
            Full traceability, human control, and defensible outputs ready for viva, review, or publication.
          </p>
        </motion.div>

        {/* 2×2 Tile Grid */}
        <div className="grid sm:grid-cols-2 gap-5 md:gap-6">
          {benefits.map((b, i) => (
            <motion.div
              key={b.title}
              custom={i}
              variants={tileVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-40px' }}
              className="group"
            >
              <div className="relative h-full rounded-2xl border border-border/60 bg-card overflow-hidden transition-all duration-300 hover:border-primary/25 hover:shadow-xl hover:shadow-primary/5 hover:-translate-y-1">
                {/* Gradient strip top */}
                <div
                  className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${b.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
                  aria-hidden="true"
                />

                <div className="p-6 sm:p-7 md:p-8 flex flex-col h-full">
                  {/* Top row: icon + stat */}
                  <div className="flex items-start justify-between mb-5">
                    <div className={`h-11 w-11 rounded-xl ${b.iconBg} flex items-center justify-center transition-transform duration-300 group-hover:scale-110`}>
                      <b.icon className="h-5 w-5" aria-hidden="true" />
                    </div>
                    <div className="text-right">
                      <span className="block text-2xl md:text-3xl font-bold tracking-tight text-foreground leading-none">
                        {b.stat}
                      </span>
                      <span className="text-[11px] text-muted-foreground/70 font-medium uppercase tracking-wide">
                        {b.statLabel}
                      </span>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-base md:text-lg font-semibold tracking-tight text-foreground mb-3 leading-snug">
                    {b.title}
                    {b.footnote && <span className="text-primary">*</span>}
                  </h3>

                  {/* Body */}
                  <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                    {b.body}
                  </p>

                  {/* Footnote */}
                  {b.footnote && (
                    <p className="text-[11px] text-muted-foreground/50 italic mt-4">
                      {b.footnote}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
